/**
 * READING THE `otpauth://` URI THE SERVER ISSUES. **CM-024.**
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────────────────────────
 *
 * `MfaEnrolmentScreen` displayed `fixtureMfaEnrolment.secretBase32` — a fixed, published Base32
 * string — as the manual key, to every user, while confirming against the server's real challenge.
 * Two defects in one line: enrolment could never verify, because the code came from the wrong
 * secret; and anyone who did scan it enrolled a secret this repository publishes.
 *
 * The server already returned the provisioning URI, which carries the real secret and the account
 * label. What was missing was a reader for it.
 *
 * ── AND WHY IT IS PARSED RATHER THAN ASKED FOR FIELD BY FIELD ───────────────────────────────────
 *
 * The URI is the format authenticator applications consume, so it has to be exactly right anyway,
 * and it is what the QR encodes. Asking the server for `{secret, label, issuer}` alongside it would
 * be two representations of one fact — and the day they disagree, the screen shows a manual key that
 * does not match the QR beside it. One source, read.
 */

/** What the screen needs from the URI. Nothing else is extracted, and nothing is stored. */
export interface ProvisioningDetails {
  /** The account name an authenticator will display. `issuer:account`, as the URI spells it. */
  readonly accountLabel: string;
  /** The shared secret, Base32. Shown once, as the manual key. */
  readonly secretBase32: string;
  /**
   * The URI AS IT ARRIVED, for the QR to encode. **2026-10-25.**
   *
   * A TOTP QR encodes exactly this string — not the secret alone — because the label, the issuer
   * and the digit/period parameters all have to reach the authenticator with it. Re-assembling it
   * from the parsed parts would be a second construction of a value we already hold, and the two
   * could only ever differ by one of them being wrong.
   *
   * IT CONTAINS THE SHARED SECRET, the same as `secretBase32` beside it. Its one consumer is the
   * local encoder in `<QrCode>`; it is never logged, stored or put in an address.
   */
  readonly uri: string;
}

/**
 * Parses `otpauth://totp/<label>?secret=…&issuer=…`.
 *
 * Returns `null` rather than a partial answer. A screen given half a provisioning URI would display
 * an account label with no key, which reads as "the key is missing" rather than "this did not
 * arrive" — and the user would enrol nothing while believing they had.
 */
export function readProvisioning(uri: string): ProvisioningDetails | null {
  let parsed: URL;
  try {
    parsed = new URL(uri);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'otpauth:') return null;

  // `otpauth://totp/Label` — the host is `totp` and the label is the path. Decoded, because the
  // label carries a colon between issuer and account and is percent-encoded on the way out.
  if (parsed.host !== 'totp') return null;
  const accountLabel = decodeURIComponent(parsed.pathname.replace(/^\//u, ''));
  const secretBase32 = parsed.searchParams.get('secret') ?? '';

  if (accountLabel === '' || secretBase32 === '') return null;
  // `uri` is the ORIGINAL argument, not `parsed.toString()`: `URL` normalises, and an authenticator
  // reading a re-serialised URI should not be the first place we discover what it normalised.
  return { accountLabel, secretBase32, uri };
}
